import React, {useContext, useEffect, useRef, useState} from 'react';
import {Badge, Button, Card, OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import cellEditFactory, {Type} from 'react-bootstrap-table2-editor';
import {DateTime} from "luxon";
import Table, {
    buildMultiSelectFilterRenderer, buildSelectFilter, likeCompMultiSelectFilter, numberFormatter, tableTextFilter
} from "../Table/Table";

import {
    DATE_LOCALE, WINT_AI_CONTROL_URL, WINT_SITES_ROUTE, WINT_WATER_SYSTEMS_ROUTE,
} from "../../Constants/constants";
import 'react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';
import './AlertsTable.css'
import {store} from '../../Store/store'
import {
    SET_IS_REQUEST_IN_PROGRESS, UPDATE_EXCLUDED_CHECKBOX_ARR
} from "../../Store/ActionTypes/General/actionTypes";
import ReasonHistoryDropdownEditor from "./ReasonHistoryDropdownEditor";
import ConfirmationHistoryDropdownEditor from "./ConfirmationHistoryDropdownEditor";
import {RiFilterFill} from "react-icons/ri";
import ValuesFilterModal from "../../Pages/Monitoring/ValuesFilterModal";
import {RiFilterOffFill} from "react-icons/ri";

export default function AlertsHistoryTable({
                                               confimrationData,
                                               reasons,
                                               leakTypes,
                                               notificationLevels,
                                               data,
                                               monitoringService,
                                               setAlertTableData,
                                               getEventHistorySummaryData,
                                               confirmations,
                                               setFilteredData,
                                               originalAlertTableData,
                                               filteredData,
                                               setExcludedPopupText,
                                               setOriginalAlertTableData,
                                               handleOnEdit,
                                               setShowExcludedPopup,
                                               isSetButtonClicked,
                                               dateApplied,
                                               isTableMounted

                                           }) {
    const {dispatch} = useContext(store);
    const stateObj = useContext(store);
    const [vfmShow, setVfmShow] = useState(false);
    const [isExcludedAllClicked, setIsExcludedAllClicked] = useState(false);
    const [isIncludedAllClicked, setIsIncludedAllClicked] = useState(false);
    const [isExcludeRequestInProgress, setIsExcludeRequestInProgress] = useState(false);
    const clearFiltersButtonRef = useRef(null);
    const [tableFilteredData, setTableFilteredData] = useState([]);
    const NUMBER_OF_FILTERS = 7;
    const NOT_FOUND = -1;

    // region Formatters

    const confirmationFilterOptions = confimrationData.filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i);

    const reasonsFilterOptions = reasons.map((reason) => {
        return ({value: reason.id, label: reason.text});
    });

    const leakTypeFilterOptions = leakTypes.map((leakType) => {
        return ({value: leakType.label, label: leakType.label});
    })

    const notificationLevelFilterOptions = notificationLevels.map((notificationLevel) => {
        return ({value: notificationLevel.label, label: notificationLevel.label});
    })

    const reasonsOptions = confimrationData.map((reason) => {
        return ({...reason, label: reason.text});
    });

    const rawDataFilterOptions = {
        false: 'False', true: 'True'
    };

    const excludedFilterOptions = {
        false: 'Included', true: 'Excluded'
    };

    const initStateData = () => {
        let arr = [];
        if (data) {
            data.forEach(obj => {
                if (getConfirmationEnum(obj.confirmation) === 'CONFIRMED_FALSE_ALERT') {
                    arr.push({state: true, id: obj.id, isFalseAlert: true});
                } else {
                    arr.push({state: obj.excluded, id: obj.id, isFalseAlert: false});
                }
            })
            updateExcludedCheckBoxArr(arr);
        }
    }

    useEffect(() => {
        initStateData();
    }, [data])

    const numberSortFunc = (a, b, order) => {
        if (order === 'asc') {
            return b - a;
        }
        return a - b; // desc
    }

    const dateSorter = (a, b, order) => {
        return order === 'asc' ? Date.parse(a) - Date.parse(b) : Date.parse(b) - Date.parse(a);
    }

    const excludedFormatter = (cell, row) => {
        return <ExcludeEditor cell={cell} row={row} setIsRequestInProgress={setIsRequestInProgress}
                              monitoringService={monitoringService} getReasonEnum={getReasonEnum}
                              getConfirmationEnum={getConfirmationEnum} getEventHistorySummaryData={getEventHistorySummaryData}
                              data={data} setAlertTableData={setAlertTableData} updateExcludedCheckBoxArr={updateExcludedCheckBoxArr}
        />;
    }

    const potentialLeakVolumeFormatter = (cell) => {
        return cell ? Number(cell).toLocaleString() : 0;
    }

    const durationFormatter = (cell) => {
        if (!cell) return '00:00:00';
        let numOfSecondsInDay = 60 * 60 * 24;
        let days = Math.floor(cell / numOfSecondsInDay);
        let time = new Date(cell * 1000).toISOString().substr(11, 8);
        let dayStr;
        if (days === 1) {
            dayStr = 'day';
        } else if (days > 1) {
            dayStr = 'days';
        }

        return (days > 0) ? `${days} ${dayStr}, ${time}` : `${time}`;
    }

    const waterSystemFormatter = (cell, row) => {
        let href = `${WINT_AI_CONTROL_URL}/${WINT_SITES_ROUTE}/${row.siteId}/${WINT_WATER_SYSTEMS_ROUTE}/${row.waterSystemId}`;
        return <a target="_blank" rel="noreferrer" href={href}>{row.waterSystemName}</a>;
    }

    const siteFormatter = (cell, row) => {
        let href = `${WINT_AI_CONTROL_URL}/${WINT_SITES_ROUTE}/${row.siteId}`;
        return <a target="_blank" rel="noreferrer" href={href}>{row.siteName}</a>;
    }

    const rawDataFormatter = (cell) => {
        let displayValue = cell ? 'True' : 'False';
        return displayValue;
    }

    const severityFormatter = (cell, row) => {
        let variant;
        if (cell === 'Minor') {
            variant = 'success';
        }
        if (cell === 'Moderate') {
            variant = 'warning';
        }
        if (cell === 'Major') {
            variant = 'danger';
        }
        return <Badge variant={variant}>{cell}</Badge>;
    }

    const confirmationFormatter = (cell) => {
        return confirmationEditor.options.filter(conf => {
            return conf.value === Number(cell);
        })[0]?.label;
    }

    const reasonsFormatter = (cell, row) => {
        const reasonValue = reasonEditor.options.filter(reason => {
            return reason.id === row.reason;
        });
        return reasonValue[0]?.label || "";
    }

    // endregion

    // region Filters

    const filtersRefs = useRef([]);
    const tierFilterRef = useRef();
    const assigneeFilterRef = useRef();
    const confirmationFilterRef = useRef();
    const reasonFilterRef = useRef();
    const leakTypeFilterRef = useRef();
    const notificationLevelFilterRef = useRef();

    // endregion

    // region Table Editors

    const confirmationEditor = {
        type: Type.SELECT, options: confirmationFilterOptions
    };

    const reasonEditor = {
        type: Type.SELECT, options: reasonsOptions
    };

    // endregion

    // region Styles

    const colStyle = {minWidth: 100, fontSize: 14, whiteSpace: 'nowrap'};
    const headerStyle = {minWidth: 100, fontSize: 14};

    // endregion

    const setIsRequestInProgress = (state, rowId) => {
        dispatch({
            type: SET_IS_REQUEST_IN_PROGRESS, payload: {
                isRequestInProgress: {state: state, rowId: rowId}
            }
        });
    }

    const updateExcludedCheckBoxArr = (dataArr) => {
        dispatch({
            type: UPDATE_EXCLUDED_CHECKBOX_ARR, payload: dataArr
        });
    }

    // region Table Properties

    const defaultSort = {dataField: 'updateDate', order: 'desc'};

    const getConfirmationEnum = (confirmationId) => {
        const result = confirmations.filter((obj) => obj.id === confirmationId);
        return result[0] ? result[0].value : 'UNKNOWN';

    }

    const getReasonEnum = (reasonId) => {
        const result = reasons.filter((obj) => obj.id === reasonId);
        const resultValue = result[0] ? result[0].text : '';
        let enumText = resultValue === '' ? 'UNKNOWN' : resultValue.replaceAll(' ', '_').toUpperCase();
        return enumText;
    }

    const cellEdit = cellEditFactory({
        mode: 'click', blurToSave: true, autoSelectText: true, beforeSaveCell: async (oldValue, newValue, row, column) => {
            if (newValue === 'reasonChanged') {
                return;
            }
            if (oldValue === newValue) return;
            if (column.dataField === "confirmation" && newValue !== 0) return;
            row[column.dataField] = newValue;
            if (column.dataField === "confirmation") {
                row['reason'] = reasonsOptions.filter((option) => option.value === newValue)[0].id;
            }
            setIsRequestInProgress(true, row.id);
            monitoringService.updateEditableFields(row.id, row.assignee, row.remarks, getConfirmationEnum(row.confirmation), getReasonEnum(row.reason), row.exclude, row.communication).then().then(() => {
                setIsRequestInProgress(false, row.id);
            }).catch(err => {
                setIsRequestInProgress(false, row.id);
                console.log("err === ", err);
            })

        }
    });
    buildMultiSelectFilterRenderer()

    const handleTableColumnFilter = (data) => {
        setFilteredData(data);
    }

    const excludeAll = async () => {
        const isExcludedCheckboxDisabledArr = stateObj.state.isExcludedCheckboxDisabledArr;
        let newData = data.slice();
        let newOriginalData = originalAlertTableData.slice();
        let idsArray = [];
        if (newData.length === newOriginalData.length) {
            filteredData.forEach(filteredObj => {
                let newDataIndex = newData.findIndex(dataObj => dataObj.id === filteredObj.id);
                let originalDataIndex = newOriginalData.findIndex(originalDataObj => originalDataObj.id === filteredObj.id);
                if (!(newDataIndex === NOT_FOUND)) {
                    newData[newDataIndex] = {...filteredObj, excluded: true};
                    idsArray.push(newData[newDataIndex].id)
                }
                if (!(originalDataIndex === NOT_FOUND)) {
                    newOriginalData[originalDataIndex] = {...filteredObj, excluded: true};
                }
            })
        } else {
            filteredData.forEach(filteredObj => {
                let newDataIndex = newData.findIndex(dataObj => dataObj.id === filteredObj.id);
                let originalDataIndex = newOriginalData.findIndex(originalDataObj => originalDataObj.id === filteredObj.id);
                if (!(newDataIndex === NOT_FOUND)) {
                    newData[newDataIndex] = {...filteredObj, excluded: true};
                }
                if (!(originalDataIndex === NOT_FOUND)) {
                    newOriginalData[originalDataIndex] = {...filteredObj, excluded: true};
                }
            })
            isExcludedCheckboxDisabledArr.forEach(isExcludedCheckboxDisabledArrObj => {
                let newArr = filteredData.filter(filteredDataObj => filteredDataObj.id === isExcludedCheckboxDisabledArrObj.id && !isExcludedCheckboxDisabledArrObj.state && !isExcludedCheckboxDisabledArrObj.isFalseAlert);
                if (newArr.length > 0) {
                    let index = idsArray.findIndex(idsArrayObj => idsArrayObj === newArr[0].id);
                    if (index === NOT_FOUND) {
                        idsArray.push(newArr[0].id);
                    }
                }
            })
            const newArr = isExcludedCheckboxDisabledArr.map(obj => {
                if (obj.id === newData.id) {
                    return {...obj, state: newData.excluded};
                }
                return obj;
            })
            updateExcludedCheckBoxArr(newArr);
        }
        try {
            setIsExcludeRequestInProgress(true);
            await monitoringService.updateExcludedLeaks(idsArray);
            setExcludedPopupText('');
            setAlertTableData(newData);
            setOriginalAlertTableData(newOriginalData);
            await getEventHistorySummaryData();
        } catch (e) {
            setIsExcludeRequestInProgress(false);
            setExcludedPopupText('Error excluding all, please try again later');
        }
        setIsExcludeRequestInProgress(false);
        setIsIncludedAllClicked(true);
    }

    const includeAll = async () => {
        let newData = data.slice();
        let newOriginalData = originalAlertTableData.slice();
        const isExcludedCheckboxDisabledArr = stateObj.state.isExcludedCheckboxDisabledArr;
        let idsArray = [];
        if (newData.length === newOriginalData.length) {
            filteredData.forEach(filteredObj => {
                let newDataIndex = newData.findIndex(dataObj => dataObj.id === filteredObj.id);
                let originalDataIndex = newOriginalData.findIndex(originalDataObj => originalDataObj.id === filteredObj.id);
                if (!(newDataIndex === NOT_FOUND)) {
                    newData[newDataIndex] = {...filteredObj, excluded: false};
                    idsArray.push(newData[newDataIndex].id)
                }
                if (!(originalDataIndex === NOT_FOUND)) {
                    newOriginalData[originalDataIndex] = {...filteredObj, excluded: false};
                }
            })
        } else {
            filteredData.forEach(filteredObj => {
                let newDataIndex = newData.findIndex(dataObj => dataObj.id === filteredObj.id);
                let originalDataIndex = newOriginalData.findIndex(originalDataObj => originalDataObj.id === filteredObj.id);
                if (!(newDataIndex === NOT_FOUND)) {
                    newData[newDataIndex] = {...filteredObj, excluded: false};
                }
                if (!(originalDataIndex === NOT_FOUND)) {
                    newOriginalData[originalDataIndex] = {...filteredObj, excluded: false};
                }
            })
            isExcludedCheckboxDisabledArr.forEach(isExcludedCheckboxDisabledArrObj => {
                let newArr = filteredData.filter(filteredDataObj => filteredDataObj.id === isExcludedCheckboxDisabledArrObj.id && isExcludedCheckboxDisabledArrObj.state && !isExcludedCheckboxDisabledArrObj.isFalseAlert);
                if (newArr.length > 0) {
                    let index = idsArray.findIndex(idsArrayObj => idsArrayObj === newArr[0].id);
                    if (index === NOT_FOUND) {
                        idsArray.push(newArr[0].id);
                    }
                }
            })
            const newArr = isExcludedCheckboxDisabledArr.map(obj => {
                if (obj.id === newData.id) {
                    return {...obj, state: newData.excluded};
                }
                return obj;
            })
            updateExcludedCheckBoxArr(newArr);
        }
        try {
            setIsExcludeRequestInProgress(true);
            await monitoringService.updateIncludedLeaks(idsArray);
            setExcludedPopupText('');
            setAlertTableData(newData);
            setOriginalAlertTableData(newOriginalData);
            await getEventHistorySummaryData();
        } catch (e) {
            setIsExcludeRequestInProgress(false);
            setExcludedPopupText('Error including all, please try again later');
        }
        setIsExcludeRequestInProgress(false);
        setIsIncludedAllClicked(true);
    }

    const resetFilters = () => {
        if (filtersRefs.current.length > NUMBER_OF_FILTERS) {
            filtersRefs.current.splice(0, NUMBER_OF_FILTERS);
        }
        filtersRefs.current.forEach(currFilter => {
            if (currFilter) currFilter('');
        })

        confirmationFilterRef.current.resetFilter();
        reasonFilterRef.current.resetFilter();
        leakTypeFilterRef.current.resetFilter();
        notificationLevelFilterRef.current.resetFilter();
    }

    const columns = [
        {dataField: 'siteName', isDefault: true, text: 'Site', headerStyle: headerStyle, style: colStyle, sort: true, filter: tableTextFilter(filtersRefs), editable: false, formatter: siteFormatter},
        {dataField: 'waterSystemName', text: 'Water System', headerStyle: headerStyle, style: colStyle, sort: true, filter: tableTextFilter(filtersRefs), editable: false, formatter: waterSystemFormatter},
        {dataField: 'waterSystemId', text: 'Water System Id', headerStyle: headerStyle, style: colStyle, sort: true, sortFunc: numberSortFunc, filter: tableTextFilter(filtersRefs), editable: false},
        {dataField: 'rawData', text: 'Raw Data', headerStyle: headerStyle, style: colStyle, sort: true, editable: false, formatter: rawDataFormatter, filter: buildSelectFilter(filtersRefs, rawDataFilterOptions)},
        {dataField: 'leakType', headerStyle: headerStyle, text: 'Severity', style: colStyle, sort: true, filter: likeCompMultiSelectFilter, filterRenderer: buildMultiSelectFilterRenderer(leakTypeFilterRef, leakTypeFilterOptions), editable: false, formatter: severityFormatter},
        {dataField: 'level', text: 'Notification Level', headerStyle: headerStyle, style: colStyle, sort: true, filter: likeCompMultiSelectFilter, filterRenderer: buildMultiSelectFilterRenderer(notificationLevelFilterRef, notificationLevelFilterOptions), editable: false},
        {dataField: 'leakStartTime', text: 'Leak Start Time', headerStyle: headerStyle, style: colStyle, sort: true, sortFunc: dateSorter, filter: tableTextFilter(filtersRefs), editable: false},
        {dataField: 'endOfLeak', text: 'End Of Leak', headerStyle: headerStyle, style: colStyle, sort: true, sortFunc: dateSorter, filter: tableTextFilter(filtersRefs), editable: false},
        {dataField: 'leakFlowRate', text: 'Flow Rate (L/h)', headerStyle: headerStyle, style: colStyle, formatter: numberFormatter, sortFunc: numberSortFunc, sort: true, editable: false},
        {dataField: 'readableActualDuration', text: 'Duration', headerStyle: headerStyle, style: colStyle, sort: true, editable: false, formatter: durationFormatter},
        {dataField: 'actualLeakVolume', text: 'Volume (L)', headerStyle: headerStyle, style: colStyle, formatter: numberFormatter, sortFunc: numberSortFunc, sort: true, editable: false},
        {dataField: 'readablePotentialLeakDuration', text: 'Potential Duration', headerStyle: headerStyle, style: colStyle, sortFunc: numberSortFunc, sort: true, editable: false, formatter: durationFormatter},
        {dataField: 'potentialLeakVolume', text: 'Potential Volume (L)', headerStyle: headerStyle, style: colStyle, sortFunc: numberSortFunc, sort: true, editable: false, formatter: potentialLeakVolumeFormatter},
        {
            dataField: 'confirmation', adminColumn: true, text: 'Confirmation', headerStyle: headerStyle, style: colStyle, sort: true, filter: likeCompMultiSelectFilter, filterRenderer: buildMultiSelectFilterRenderer(confirmationFilterRef, confirmationFilterOptions),
            editorRenderer: (editorProps, value, row, column) => {
                return <ConfirmationHistoryDropdownEditor handleOnEdit={handleOnEdit} value={value}
                                                          updateExcludedCheckBoxArr={updateExcludedCheckBoxArr}
                                                          row={row}
                                                          getConfirmationEnum={getConfirmationEnum}
                                                          options={confirmationFilterOptions} {...editorProps} />;
            },
            formatter: confirmationFormatter,
            csvFormatter: confirmationFormatter
        }, // When saving the reason to the DB need to save the reason id and not text
        {
            dataField: 'reason', adminColumn: true, text: 'Reason', headerStyle: headerStyle, style: colStyle, sort: true, filter: likeCompMultiSelectFilter, filterRenderer: buildMultiSelectFilterRenderer(reasonFilterRef, reasonsFilterOptions), editorRenderer: (editorProps, value, row, column) => {
                return <ReasonHistoryDropdownEditor row={row} column={column} handleOnEdit={handleOnEdit}
                                                    value={value} toggleExcludedCheckBox={updateExcludedCheckBoxArr}
                                                    reasonsOptions={reasonsOptions} {...editorProps}
                                                    reasons={reasons} confirmations={confirmations}
                                                    setIsRequestInProgress={setIsRequestInProgress}
                                                    getConfirmationEnum={getConfirmationEnum}
                                                    getReasonEnum={getReasonEnum}
                                                    monitoringService={monitoringService}/>;
            }, formatter: reasonsFormatter, csvFormatter: reasonsFormatter
        }, {
            dataField: 'excluded', adminColumn: true, text: 'Excluded', headerStyle: headerStyle, style: colStyle, sort: true, editable: false, filter: buildSelectFilter(filtersRefs, excludedFilterOptions), formatter: excludedFormatter, csvFormatter: reasonsFormatter
        },]

    // endregion

    useEffect(() => {
        setIsRequestInProgress(false);
    }, [])

    useEffect(() => {
        if (isExcludedAllClicked) {
            setShowExcludedPopup(true);
            setIsExcludedAllClicked(false);
        }
    }, [isExcludedAllClicked])

    useEffect(() => {
        if (isIncludedAllClicked) {
            setShowExcludedPopup(true);
            setIsIncludedAllClicked(false);
        }
    }, [isIncludedAllClicked])

    // endregion
    return (<>
        {(isSetButtonClicked || dateApplied && isTableMounted) ? <Spinner animation="border" variant="primary"/> :
            data ?
                <Card className='rounded-0' style={{overflowX: 'auto', height: '41em'}}>
                    <Table
                        data={data}
                        pageSize={14}
                        columns={columns}
                        handleTableColumnFilter={handleTableColumnFilter}
                        setTableFilteredData={setTableFilteredData}
                        additionalToolbarComponent={
                            <>
                                <Card.Header style={{background: 'white', textAlign: 'left', height: '50px'}}>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Filter Displayed Leaks</Tooltip>}>
                                        <Button size={"sm"} variant='link' style={{color: 'black', lineHeight: '0'}} onClick={() => setVfmShow(true)}>
                                            <RiFilterFill size={25}/>
                                        </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Clear Table Filters</Tooltip>}>
                                        <Button size={"sm"} variant='link' style={{color: 'black', lineHeight: '0'}} ref={clearFiltersButtonRef} onClick={resetFilters} className={"mr-2"}><RiFilterOffFill size={25}/></Button>
                                    </OverlayTrigger>
                                    <Button size={"sm"} className='mr-2' onClick={excludeAll}>
                                        Exclude All
                                    </Button>
                                    <Button size={"sm"} onClick={includeAll}>
                                        Include All
                                    </Button>
                                    {
                                        isExcludeRequestInProgress ?
                                            <Spinner className='ml-2' size={"sm"} animation="border" variant="primary"/> : <></>
                                    }
                                </Card.Header>
                                <ValuesFilterModal handleTableColumnFilter={handleTableColumnFilter} setAlertTableData={setAlertTableData} data={originalAlertTableData} show={vfmShow} filteredData={filteredData} setShow={setVfmShow} tableFilteredData={tableFilteredData}
                                                   setTableFilteredData={setTableFilteredData}/>
                            </>
                        }
                        csvFileName={`Alerts_Report_${DateTime.local().toLocaleString({locale: DATE_LOCALE})}.csv`}
                        resetFilters={resetFilters}
                        defaultSort={defaultSort}
                        cellEdit={cellEdit}/>
                </Card> : null
        }
    </>);
}

function useForceUpdate() {
    const [value, setValue] = useState(0); // integer state
    return () => setValue(value => value + 1); // update the state to force render
}

function ExcludeEditor({row, cell, monitoringService, setIsRequestInProgress, getConfirmationEnum, getReasonEnum, getEventHistorySummaryData, data, setAlertTableData, updateExcludedCheckBoxArr}) {
    const stateObj = useContext(store);
    const isRequestInProgress = stateObj.state.isRequestInProgress;
    const forceUpdate = useForceUpdate();
    const excludeCheckboxId = `${row.id}_exclude_checkbox`;
    const leakId = row.id;
    const assigneeField = row.assignee;
    const remarksField = row.remarks;
    const confirmationField = row.confirmation;
    const isExcludedCheckboxDisabledArr = stateObj.state.isExcludedCheckboxDisabledArr;
    const communicationField = row.communication;
    const reasonField = row.reason;

    const getCheckboxStateObj = () => {
        if (isExcludedCheckboxDisabledArr) {
            const filterResult = isExcludedCheckboxDisabledArr.filter(obj => obj.id === row.id);
            const checkboxStateObj = filterResult[0];
            return checkboxStateObj;
        } else {
            return cell;
        }

    }

    const [isChecked, setIsChecked] = useState(getCheckboxStateObj());

    const checkCheckBoxStatus = () => {
        const checkboxStateObj = getCheckboxStateObj();
        if (!checkboxStateObj) {
            return false;
        }
        const checkboxDisabled = isRequestInProgress.state && isRequestInProgress.rowId === row.id || checkboxStateObj.isFalseAlert && checkboxStateObj.id === row.id;
        return checkboxDisabled;
    }

    const checkCheckedMark = () => {
        const checkboxStateObj = getCheckboxStateObj();
        if (!checkboxStateObj) {
            return isChecked;
        }
        if (checkboxStateObj.isFalseAlert) {
            return true;
        }
        return checkboxStateObj.state;
    }

    const updateCheckboxArrObj = (state) => {
        const newArr = isExcludedCheckboxDisabledArr.map(obj => {
            if (obj.id === row.id) {
                return {...obj, state: state};
            }
            return obj;
        })
        updateExcludedCheckBoxArr(newArr);
    }

    const saveOnClick = async () => {
        const currentCheckBoxStatus = checkCheckedMark();
        setIsChecked(!currentCheckBoxStatus);
        let newValue = !currentCheckBoxStatus;
        monitoringService.updateEditableFields(leakId, assigneeField, remarksField, getConfirmationEnum(confirmationField), getReasonEnum(reasonField), newValue, communicationField)
            .then(() => {
                setIsRequestInProgress(false, row.id);
                updateCheckboxArrObj(newValue);
                getEventHistorySummaryData();
            }).catch(error => {
            setIsRequestInProgress(false, row.id);
            console.log(error);
        })
    }
    if (!isExcludedCheckboxDisabledArr) {
        return <Spinner animation="border" variant="primary"/>;
    } else {
        return (<>
            <input type="checkbox" id={excludeCheckboxId} onClick={forceUpdate}
                   disabled={checkCheckBoxStatus()}
                   checked={checkCheckedMark()}
                   onChange={() => saveOnClick(excludeCheckboxId, 1)}/>
        </>);
    }
}
